import React, { createContext, useState, useContext, useEffect, ReactNode, useCallback } from 'react';
import apiClient from '../services/api'; // 导入 apiClient
// Import shared User type and Role type
import { User } from '../types/user';
import { UserRole } from '../types/enums'; // Import UserRole if needed for context value type

// Remove local User interface definition
// interface User { ... }

interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  user: User | null; // Use imported User type
  authLoading: boolean; // 添加加载状态
  login: (authData: { token: string; user: User }) => void; // login 参数包含 token 和 user
  logout: () => void;
}

// 创建 Context，并提供一个默认值
const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null); // 添加 user state
  const [authLoading, setAuthLoading] = useState<boolean>(true); // 初始为 true

  // 抽离 logout 逻辑以便复用
  const performLogout = useCallback(() => {
    console.log("Performing logout...");
    localStorage.removeItem('authToken');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // 抽离验证 Token 和获取用户信息的逻辑
  const verifyAuth = useCallback(async () => {
    console.log("Verifying auth token...");
    setAuthLoading(true); // 开始验证，设置加载状态
    try {
      const response = await apiClient.get('/me'); // 调用 /api/me
      const userData = response.data;
      if (userData) {
        console.log("Auth verified, user data:", userData);
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        console.log("Auth verification failed: No user data from /me");
        performLogout(); // 如果 /me 没返回有效数据，则登出
      }
    } catch (error) {
      console.error("Auth verification failed:", error);
      performLogout(); // Token 无效或请求失败，执行登出
    } finally {
      setAuthLoading(false); // 验证结束，取消加载状态
    }
  }, [performLogout]); // 依赖 performLogout

  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    if (storedToken) {
      console.log("Found token in localStorage, verifying...");
      // 重要：设置 axios 实例的默认 token，以便 verifyAuth 中的请求能携带它
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      setToken(storedToken); // 先设置 token state
      verifyAuth(); // 调用异步验证函数
    } else {
      console.log("No token found, setting authLoading to false.");
      setAuthLoading(false); // 没有 token，直接结束加载
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, []); // 这个 effect 只在组件挂载时运行一次

  const login = (authData: { token: string; user: User }) => {
    console.log("Login process started:", authData.user);
    localStorage.setItem('authToken', authData.token);
    // 设置 axios 实例的默认 token
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${authData.token}`;
    setToken(authData.token);
    setUser(authData.user);
    setIsAuthenticated(true);
    setAuthLoading(false); // 登录成功，验证结束
    console.log("AuthContext: User logged in and state updated", authData.user);
  };

  const logout = () => {
    console.log("Logout process started...");
    // 清除 axios 实例的默认 token
    delete apiClient.defaults.headers.common['Authorization'];
    performLogout();
    setAuthLoading(false); // 登出后也要结束加载状态
    console.log("AuthContext: User logged out.");
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, token, user, authLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// 自定义 Hook，方便在组件中使用 AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth 必须在 AuthProvider 内部使用');
  }
  return context;
}; 